import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	type ReactNode,
} from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import {
	TopNav,
	TopNavStart,
	TopNavMiddle,
	TopNavEnd,
	SideNavToggleButton,
} from '@atlaskit/navigation-system';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { AnyEntryPoint } from '@atlassian/entry-point-types/types';
import { UniversalCreateButton } from '@atlassian/universal-create/UniversalCreateButton';

import { GlobalTopNavigationAnalyticsContext } from '../../common/analytics/context';
import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import type {
	GlobalTopNavContext,
	GlobalTopNavigationProps,
	GlobalTopNavSlot,
	GlobalTopNavSlotProps,
	GlobalTopNavSlotWithoutPlatformComponent,
	NotificationsProps,
	ProfileComponentProps,
	UseSideNavToggleStateResult,
} from '../../common/types';
import { AsyncIntlProvider } from '../../common/utils/locale';
import {
	useLocalToggleState,
	useNetworkToggleState,
} from '../../controllers/useSideNavToggleState';
import { AppHome } from '../app-home/AppHome';
import { AppSwitcher } from '../app-switcher/AppSwitcher';
import { Chat } from '../chat/Chat';
import { Help } from '../help/Help';
import { Notifications } from '../notifications/Notifications';
import type { Profile_AtlassianGlobalTopNavigation$key } from '../profile/__generated__/Profile_AtlassianGlobalTopNavigation.graphql';
import { Profile } from '../profile/Profile';
import { Search } from '../search/Search';
import { Settings } from '../settings/Settings';
import type {
	GlobalTopNavigation$data,
	GlobalTopNavigation$key,
} from './__generated__/GlobalTopNavigation.graphql';
import type { GlobalTopNavigationView$key } from './__generated__/GlobalTopNavigationView.graphql';
import { messages } from './messages';

const ProfileQueryRefContext = createContext<Profile_AtlassianGlobalTopNavigation$key | null>(null);

/**
 * The Profile component exposed to app slots. Its Relay reference is read from
 * GTN's own data tree, leaving apps responsible only for menu configuration.
 */
function ProfileWithPlatformData<TEntryPoint extends AnyEntryPoint>(
	props: ProfileComponentProps<TEntryPoint>,
) {
	const queryRef = useContext(ProfileQueryRefContext);

	return <Profile {...props} queryRef={queryRef} />;
}

/**
 * The props shared by both store-specific wrappers and the presentational nav.
 */
interface TopNavViewProps extends Omit<
	GlobalTopNavigationProps,
	'customizationStore' | 'queryRef'
> {
	/**
	 * The already-read `GlobalTopNavigation` fragment data, which carries the
	 * composed `useSideNavToggleStateQuery` spread. Only the network store reads
	 * it; the local store ignores it entirely.
	 */
	queryRef: GlobalTopNavigation$data;
}

export function GlobalTopNavigation(props: GlobalTopNavigationProps): JSX.Element {
	const { intlMode = 'async' } = props;
	// The provider has to sit outside everything that calls `useIntl`, so it
	// wraps the store wrappers rather than living inside the view. The analytics
	// context sits outermost so that every event fired from the subtree — including
	// any fired by consumer-supplied slots — carries the package and source metadata.
	return (
		<GlobalTopNavigationAnalyticsContext>
			<AsyncIntlProvider disabled={intlMode === 'inherit'}>
				<GlobalTopNavigationInner {...props} />
			</AsyncIntlProvider>
		</GlobalTopNavigationAnalyticsContext>
	);
}

function GlobalTopNavigationInner(props: GlobalTopNavigationProps): JSX.Element {
	const { queryRef, customizationStore = 'local', intlMode: _intlMode, ...rest } = props;

	// Read the navigation customization data from the caller's query.
	// The caller is expected to spread `...GlobalTopNavigation` into a query.
	//
	// The actual `navPlatSidebarData` selection lives behind the composed
	// `...useSideNavToggleStateQuery` fragment (owned by the toggle-state
	// controller), and is skipped via `@skip(if: $skipNavPlatSidebarData)`.
	// GSN's `GlobalSideNavigation` fragment selects the same field and applies the
	// same skip; the two must stay in sync, as GraphQL field merging fetches a field
	// if any selection of it is unconditional.
	// Callers should set `skipNavPlatSidebarData` to `true` when the user is
	// anonymous (no personal customization to load) OR when `customizationStore`
	// is `'local'` (customization is read from `localStorage`, not the network).
	// No local @argumentDefinitions on the `...GlobalTopNavigationView` spread:
	// child fragments read ordinary query-level variables, which are in scope
	// for every spread into the same operation.
	//
	// `collabContextRoutingAri` is required. Notifications is a default slot
	// (shown in every app), so the unread-count field is always selected and
	// the caller's query must declare the variable and pass a string ARI.
	//
	// `appSwitcherProductAri`/`skipAppSwitcherUnitProfile` are optional: callers
	// that don't need the unit profile (App Home's unit title, or App Switcher's
	// unit-tile icon) can skip the field, or omit declaring the variables
	// entirely if nothing in the tree spreads `...AppHome` or `...AppSwitcher`.
	const data = useFragment<GlobalTopNavigation$key>(
		graphql`
			fragment GlobalTopNavigation on Query {
				...useSideNavToggleStateQuery
				# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads -- spread through
				# unread on purpose: this wrapper only forwards queryRef on to GlobalTopNavView, which
				# reads GlobalTopNavigationView data itself.
				...GlobalTopNavigationView
			}
		`,
		queryRef,
	);

	// Branch at the COMPONENT boundary, not inside a hook. Each wrapper calls
	// exactly one store hook, unconditionally — so the Rules of Hooks hold
	// statically (and the eslint hooks plugin can actually see it), and a change
	// to `customizationStore` correctly swaps stores instead of being silently
	// ignored.
	//
	// This mirrors how GSN picks between `<LocalStore>` and `<NetworkStore>`.
	// @see platform/packages/navigation/global-side-navigation/src/ui/GlobalSideNavigation.tsx
	// Both branches are module-level components, so this ternary is already
	// referentially stable — no `useMemo` required. Note that changing
	// `customizationStore` swaps the component type and therefore remounts the
	// subtree; see the prop's documentation.
	// Any network-backed mode (`'network'` or `'network-migration'`) uses the
	// network store; only `'local'` uses the device-local store. GTN does not yet
	// implement migration-specific behaviour, so `'network-migration'` currently
	// behaves like `'network'` here rather than silently falling back to local.
	const Store = customizationStore === 'local' ? LocalStoreTopNav : NetworkStoreTopNav;

	return <Store {...rest} queryRef={data} />;
}

/**
 * Local-storage variant. Calls `useLocalToggleState` unconditionally.
 */
function LocalStoreTopNav(props: TopNavViewProps): JSX.Element {
	const toggleState = useLocalToggleState(props.app);

	return <GlobalTopNavView {...props} {...toggleState} />;
}

/**
 * Network variant. Calls `useNetworkToggleState` unconditionally.
 */
function NetworkStoreTopNav(props: TopNavViewProps): JSX.Element {
	const toggleState = useNetworkToggleState(props.app, props.networkStorageParams, props.queryRef);

	return <GlobalTopNavView {...props} {...toggleState} />;
}

/**
 * Presentational top nav. Store-agnostic: it receives the already-resolved
 * collapsed state and writer from whichever store wrapper rendered it.
 */
function GlobalTopNavView(props: TopNavViewProps & UseSideNavToggleStateResult): JSX.Element {
	// Platform-only props are destructured so they stay out of
	// `adsTopNavProps`, which is spread onto ADS `<TopNav>`. They are consumed
	// below to build the shared slot context and read platform Relay data.
	const {
		app,
		appName,
		AppHomeSlot,
		AppSwitcherSlot,
		ChatSlot,
		closeChatPanel,
		closeHelpPanel,
		CreateSlot,
		defaultCollapsed,
		EditionAwarenessSlot,
		hasCustomAppHomeBranding = false,
		HelpSlot,
		hideSideNavToggle = false,
		isChatPanelOpen,
		isHelpPanelOpen,
		isAnonymous = false,
		networkStorageParams,
		NotificationsSlot,
		openChatPanel,
		openHelpPanel,
		setCollapsed,
		ProfileSlot,
		queryRef,
		SearchSlot,
		SettingsSlot,
		UNSAFE__TopNavEndContent,
		UNSAFE__TopNavMiddleContent,
		UNSAFE__TopNavStartContent,
		...adsTopNavProps
	} = props;
	const { formatMessage } = useIntl();

	// The single data read shared by every slot. Each platform component's
	// fragment is spread in here and read once, off the same query the caller
	// already spread `...GlobalTopNavigation` into, so apps don't need to know
	// any component's data requirements themselves (see NAVX-3388/NAVX-5411).
	//
	// The result goes onto `context` as `queryRef`, and a fragment reference
	// carries the spreads of everything selected into it — so one value serves
	// every slot. Profile also receives it through `ProfileQueryRefContext`, which
	// keeps that platform-owned data reference out of its app-facing props.
	// Adding a component's fragment below is all it takes to make its data
	// reachable; nothing else here changes as slots are added.
	// Reads the GlobalTopNavigationView/AppHome data directly here, not in the
	// outer GlobalTopNavigation wrapper above -- see the disable comment on
	// that wrapper's spread (above) for why this is split across two reads.
	// Named for the data it provides (a second, colocated read within this
	// same file), not the file name, since the file already has a same-named
	// top-level fragment -- same pattern as useSideNavToggleState.tsx.
	/* eslint-disable @atlassian/relay/graphql-naming */
	const viewData = useFragment<GlobalTopNavigationView$key>(
		graphql`
			fragment GlobalTopNavigationView on Query {
				...AppHome
				...AppSwitcher
				...Notifications
				...Profile_AtlassianGlobalTopNavigation @alias(as: "profile")
			}
		`,
		queryRef,
	);
	/* eslint-enable @atlassian/relay/graphql-naming */
	const profileQueryRef: Profile_AtlassianGlobalTopNavigation$key | null =
		viewData?.profile ?? null;

	// `useFragment` returns a new wrapper object on every render, even when
	// nothing about the data changed, so the reference cannot go into the
	// `context` memo below directly -- doing so would give `context` a new
	// identity every render and defeat `React.memo` in every consumer slot.
	// Holding it in a ref, read back through a getter, keeps `context` stable
	// while still handing slots the current reference.
	//
	// Reading a render behind would still be correct in any case: a fragment
	// reference says which records to read, and the receiving component's own
	// `useFragment` subscribes to the store, so data changes reach the UI
	// regardless of the reference's identity.
	const queryRefBox = useRef(viewData);
	queryRefBox.current = viewData;

	// When the app does not provide a NotificationsSlot, render the shared Platform panel using
	// the notification-list configuration available from GlobalTopNavContext.
	const DefaultNotificationsWithData = useCallback(
		({ app, siteId }: GlobalTopNavContext) => (
			<Notifications
				notificationListProps={{
					cloudId: siteId,
					collabContextRoutingAri: undefined,
					product: app,
				}}
				queryRef={queryRefBox.current}
			/>
		),
		[],
	);

	// renderSlotWithDefault uses one component for both paths. Delegate the context path to the
	// dedicated default wrapper and pass app-supplied notification props through unchanged.
	const NotificationsWithData = useCallback(
		<TEntryPoint extends AnyEntryPoint>(
			props: NotificationsProps<TEntryPoint> | GlobalTopNavContext,
		) =>
			'app' in props ? (
				<DefaultNotificationsWithData {...props} />
			) : (
				<Notifications {...props} queryRef={queryRefBox.current} />
			),
		[DefaultNotificationsWithData],
	);

	const { fireEvent } = useAnalyticsEvents();

	// Resolved once here and handed to every slot, so an app states each of these
	// facts a single time instead of re-deriving them inside each slot wrapper.
	// Only genuinely cross-slot facts belong here — anything a single slot needs
	// goes on that slot's props instead. Identity is read from
	// `networkStorageParams` where the app already supplies it, rather than being
	// duplicated across two props.
	//
	// The identity fields are pulled out into locals so the memo depends on
	// primitives rather than on `networkStorageParams` itself. Consumers commonly
	// pass that as an inline object literal, which is a new reference on every
	// one of their renders — depending on it directly would give `context` a new
	// identity every render and defeat `React.memo` in every consumer slot.
	const activationId = networkStorageParams?.activationId;
	const ownerAri = networkStorageParams?.ownerAri;
	const siteId = networkStorageParams?.siteId;

	const context = useMemo<GlobalTopNavContext>(
		() => ({
			activationId,
			app,
			appName,
			closeChatPanel,
			closeHelpPanel,
			hasCustomAppHomeBranding,
			isAnonymous,
			isChatPanelOpen,
			isHelpPanelOpen,
			openChatPanel,
			openHelpPanel,
			ownerAri,
			get queryRef() {
				return queryRefBox.current;
			},
			siteId,
		}),
		[
			activationId,
			app,
			appName,
			closeChatPanel,
			closeHelpPanel,
			hasCustomAppHomeBranding,
			isAnonymous,
			isChatPanelOpen,
			isHelpPanelOpen,
			openChatPanel,
			openHelpPanel,
			ownerAri,
			siteId,
		],
	);

	const handleSideNavToggle = useCallback<
		NonNullable<React.ComponentProps<typeof SideNavToggleButton>['onClick']>
	>(
		(_event, _analyticsEvent, attributes) => {
			// `isSideNavVisible` reflects the state *before* the toggle is applied.
			// So after the click, the side nav becomes collapsed when it was
			// previously visible (and vice versa).
			const collapsed = Boolean(attributes?.isSideNavVisible);

			setCollapsed(collapsed);
			// Reports the resulting state, so it always matches what was stored.
			// `itemState` is what the unified instrumentation spec keys task
			// sessions off: `expanded` opens one and `collapsed` closes it.
			fireEvent('ui.sideNav.clicked.sideNavButton', {
				itemState: collapsed ? 'collapsed' : 'expanded',
			});
		},
		[fireEvent, setCollapsed],
	);

	return (
		<TopNav {...adsTopNavProps}>
			<TopNavStart
				sideNavToggleButton={
					hideSideNavToggle ? null : (
						<SideNavToggleButton
							defaultCollapsed={defaultCollapsed}
							collapseLabel={formatMessage(messages.collapseSidebar)}
							expandLabel={formatMessage(messages.expandSidebar)}
							onClick={handleSideNavToggle}
						/>
					)
				}
			>
				{AppSwitcherSlot === undefined ? (
					<AppSwitcher {...context} productKey={context.app} />
				) : (
					renderSlot(AppSwitcherSlot, 'AppSwitcherComponent', AppSwitcher, context)
				)}
				{renderSlotWithDefault(AppHomeSlot, 'AppHomeComponent', AppHome, context)}
				{UNSAFE__TopNavStartContent}
			</TopNavStart>
			<TopNavMiddle>
				{renderSlot(SearchSlot, 'SearchComponent', Search, context)}
				{SearchSlot && renderSlot(CreateSlot, 'CreateComponent', UniversalCreateButton, context)}
				{UNSAFE__TopNavMiddleContent}
			</TopNavMiddle>
			<TopNavEnd
				persistentItems={
					fg('platform-dst-chat-panel-layout') && ChatSlot
						? renderSlot(ChatSlot, 'ChatComponent', Chat, context)
						: undefined
				}
			>
				{!SearchSlot && renderSlot(CreateSlot, 'CreateComponent', UniversalCreateButton, context)}
				{renderSlotWithoutPlatformComponent(EditionAwarenessSlot, context)}
				{!fg('platform-dst-chat-panel-layout') &&
					renderSlot(ChatSlot, 'ChatComponent', Chat, context)}
				{renderSlotWithDefault(
					NotificationsSlot,
					'NotificationsComponent',
					NotificationsWithData,
					context,
				)}
				{/*
				 * `renderSlot`, not `renderSlotWithDefault`: `HelpProps` requires an
				 * `entryPoint` or an `onClick`, neither of which is on the context, so
				 * Help has no default renderable from context alone. Omitting `HelpSlot`
				 * already rendered nothing, since `Help` returns `null` without either.
				 */}
				{renderSlot(HelpSlot, 'HelpComponent', Help, context)}
				{renderSlot(SettingsSlot, 'SettingsComponent', Settings, context)}
				<ProfileQueryRefContext.Provider value={profileQueryRef}>
					{renderSlot(ProfileSlot, 'ProfileComponent', ProfileWithPlatformData, context)}
				</ProfileQueryRefContext.Provider>
				{UNSAFE__TopNavEndContent}
			</TopNavEnd>
		</TopNav>
	);
}

/**
 * Renders a slot, applying the three-state contract described on
 * `GlobalTopNavSlot`:
 *
 *  - `null` — the app opted out. Render nothing, and keep rendering nothing
 *    once this slot gains a platform default.
 *  - omitted — render the platform default, if there is one. Slots without a
 *    default render nothing rather than a control that looks live but does
 *    nothing.
 *  - a function — hand the app the platform's component plus the shared
 *    context, and let it decide what to render.
 *
 * An omitted slot renders nothing. Slots that do have a platform default use
 * `renderSlotWithDefault` instead — rendering a stub here would give every app
 * a dead control it never asked for.
 */
function renderSlot<TComponent, ComponentName extends string>(
	Slot: GlobalTopNavSlot<TComponent, ComponentName> | undefined,
	componentName: ComponentName,
	SlotComponent: TComponent,
	context: GlobalTopNavContext,
): ReactNode {
	if (Slot === null || Slot === undefined) {
		return null;
	}

	// The cast is needed because TypeScript cannot see that a computed key of
	// type `ComponentName` satisfies the mapped type keyed on `ComponentName`.
	const slotProps = {
		[componentName]: SlotComponent,
		context,
	} as GlobalTopNavSlotProps<TComponent, ComponentName>;

	// Rendered as a component rather than called as a function, so the slot gets
	// its own fiber. That is what lets apps use hooks inside a slot, and it is
	// what makes the stable-identity requirement documented on `GlobalTopNavSlot`
	// meaningful — calling it directly would silently fold the slot's hooks into
	// this component's hook order.
	return <Slot {...slotProps} />;
}

/**
 * Renders a slot that has a platform default, applying the same three-state
 * contract as `renderSlot` — the difference being that an omitted slot renders
 * `SlotComponent` from the shared context rather than nothing.
 *
 * The constraint states the requirement that makes that possible: a component
 * with a platform default has to be renderable from context alone. That is why
 * a slot component's props are named after the context fields that feed them
 * (`appName`, `queryRef`), so the default needs no per-slot wiring here and
 * later defaults cost nothing but a call.
 */
function renderSlotWithDefault<
	TComponent extends React.ComponentType<GlobalTopNavContext>,
	ComponentName extends string,
>(
	Slot: GlobalTopNavSlot<TComponent, ComponentName> | undefined,
	componentName: ComponentName,
	SlotComponent: TComponent,
	context: GlobalTopNavContext,
): ReactNode {
	if (Slot === undefined) {
		// The cast is needed because TypeScript will not resolve JSX attributes
		// against a bare type parameter, even one constrained to a component
		// type. The constraint above is what makes it sound: `TComponent` is
		// already required to accept exactly this context.
		const DefaultComponent = SlotComponent as React.ComponentType<GlobalTopNavContext>;

		return <DefaultComponent {...context} />;
	}

	return renderSlot(Slot, componentName, SlotComponent, context);
}

/**
 * Renders only the app-owned slot content without supplying a platform component.
 * Omitted and `null` slots render nothing; a supplied slot renders as a React
 * component receiving the shared context fields as props.
 *
 * Use this sparingly, only when a slot needs a component that cannot be provided
 * by the platform, or is not expected to be provided by the platform in the
 * foreseeable future. Prefer `renderSlot` or `renderSlotWithDefault` when a
 * platform component is available, so apps can build on the shared implementation.
 */
function renderSlotWithoutPlatformComponent(
	Slot: GlobalTopNavSlotWithoutPlatformComponent | undefined,
	context: GlobalTopNavContext,
): ReactNode {
	if (Slot === undefined || Slot === null) {
		return null;
	}

	return <Slot {...context} />;
}
